import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  AlertCircle,
  User,
  FileEdit,
  Activity
} from 'lucide-react';
import api from '../../utils/api';

const AdvisorActivityPanel = ({ reservationId }) => {
  const [requests, setRequests] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'activities'

  useEffect(() => {
    fetchData(true); // Primera carga con loading

    // Polling para refrescar automáticamente cada 30 segundos (sin mostrar loading)
    const interval = setInterval(() => {
      logger.debug('Auto-refreshing activity panel...');
      fetchData(false); // Refrescos automáticos sin loading
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [reservationId]);

  const fetchData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      // Fetch change requests
      const requestsResponse = await api.get('/change-requests/my-requests');
      const filtered = requestsResponse.data.requests?.filter(req => req.reservation_id === reservationId) || [];
      setRequests(filtered);

      // Fetch activities
      const activitiesResponse = await api.get(`/reservations/${reservationId}/activities`);
      setActivities(activitiesResponse.data.activities || []);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: Clock,
        label: 'Pendiente',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-300'
      },
      approved: {
        icon: CheckCircle,
        label: 'Aprobada',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-300'
      },
      applied: {
        icon: CheckCircle,
        label: 'Aplicada',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-300'
      },
      rejected: {
        icon: XCircle,
        label: 'Rechazada',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300'
      }
    };
    return configs[status] || configs.pending;
  };

  const getSectionLabel = (section) => {
    const labels = {
      client: 'Información del Cliente',
      passengers: 'Cantidad de Pasajeros',
      passenger_info: 'Información de un Pasajero',
      itinerary: 'Itinerario',
      flights: 'Vuelos',
      hotels: 'Hoteles',
      tours: 'Tours',
      medicalAssistances: 'Asistencias Médicas',
      payment: 'Información de Pago',
      transfers: 'Traslados IN/OUT',
      cancellation: 'Cancelación de Reserva'
    };
    return labels[section] || section;
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      reservation_created: Clock,
      status_changed: Activity,
      change_request_created: FileEdit,
      change_request_approved: CheckCircle,
      change_request_rejected: XCircle,
      change_request_applied: CheckCircle,
      payment_registered: CheckCircle,
      document_uploaded: FileEdit,
      service_confirmed: CheckCircle
    };
    return icons[activityType] || Activity;
  };

  const getActivityColor = (activityType) => {
    const colors = {
      reservation_created: 'text-blue-600',
      status_changed: 'text-purple-600',
      change_request_created: 'text-orange-600',
      change_request_approved: 'text-green-600',
      change_request_rejected: 'text-red-600',
      change_request_applied: 'text-green-600',
      payment_registered: 'text-green-600',
      document_uploaded: 'text-blue-600',
      service_confirmed: 'text-teal-600'
    };
    return colors[activityType] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Cargando información...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === 'requests'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Mis Solicitudes ({requests.length})
        </button>
        <button
          onClick={() => setActiveTab('activities')}
          className={`px-4 py-2 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === 'activities'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Historial de Actividades ({activities.length})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'requests' ? (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800">Mis Solicitudes de Cambio</h3>

          {requests.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No has creado solicitudes de cambio para esta reserva</p>
            </div>
          ) : (
            requests.map((request) => {
              const statusConfig = getStatusConfig(request.status);
              const StatusIcon = statusConfig.icon;

              return (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg border ${statusConfig.borderColor} ${statusConfig.bgColor}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusIcon className={`w-5 h-5 ${statusConfig.textColor}`} />
                        <span className={`font-bold ${statusConfig.textColor}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-700">
                        Sección: {getSectionLabel(request.section_to_change)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Solicitado el: {new Date(request.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {request.request_reason && (
                    <div className="bg-white bg-opacity-50 rounded p-2 mb-2">
                      <p className="text-xs font-semibold text-gray-600">Motivo:</p>
                      <p className="text-sm text-gray-800">{request.request_reason}</p>
                    </div>
                  )}

                  {request.status === 'rejected' && request.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                      <p className="text-xs font-semibold text-red-700">Motivo del rechazo:</p>
                      <p className="text-sm text-red-800">{request.rejection_reason}</p>
                    </div>
                  )}

                  {request.status === 'approved' && !request.applied_at && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                      <p className="text-xs text-blue-700">
                        ✓ Aprobada - Los cambios se aplicarán pronto
                      </p>
                    </div>
                  )}

                  {request.status === 'applied' && (
                    <div className="bg-green-50 border border-green-200 rounded p-2 mt-2">
                      <p className="text-xs text-green-700">
                        ✓ Cambios aplicados exitosamente el {new Date(request.applied_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {request.reviewed_at && (
                    <p className="text-xs text-gray-500 mt-2">
                      Revisado el: {new Date(request.reviewed_at).toLocaleString()}
                    </p>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Historial de Actividades
          </h3>

          {activities.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No hay actividades registradas para esta reserva</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity, index) => {
                const Icon = getActivityIcon(activity.activity_type);
                const iconColor = getActivityColor(activity.activity_type);

                return (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{activity.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {activity.performed_by_name} ({activity.performed_by_role})
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(activity.created_at).toLocaleString()}
                          </span>
                        </div>

                        {activity.changes && (
                          <details className="mt-2">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
                              Ver detalles de los cambios
                            </summary>
                            <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                              {JSON.stringify(activity.changes, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvisorActivityPanel;
